export const SHELL_TOP_BAR_PX = 64;
export const SHELL_FOOTER_PX = 64;
/** Phones (below MUI `sm`) get a slimmer footer: one line, no tagline. */
export const SHELL_FOOTER_PHONE_PX = 48;
export const SHELL_PHONE_MAX_MATCH = "(max-width: 599.95px)";
export const SHELL_PHONE_MAX_MEDIA = `@media ${SHELL_PHONE_MAX_MATCH}`;

/**
 * A phone in a Safari tab (not a Home Screen app). Safari 26 paints the page
 * under its status bar and toolbar only when the document has scrolled and
 * there is page content beneath them; otherwise it leaves flat bars. So there
 * the shell starts a few pixels down the document (scrolled to on load) and
 * runs one footer height past the visible area, the footer going under the
 * toolbar.
 */
export const SHELL_PHONE_BROWSER_MATCH = `${SHELL_PHONE_MAX_MATCH} and (display-mode: browser)`;
export const SHELL_PHONE_BROWSER_MEDIA = `@media ${SHELL_PHONE_BROWSER_MATCH}`;
export const SHELL_SCROLL_RUNWAY_PX = 4;
/** How far the shell runs under the status bar (top) and the toolbar (bottom). */
export const SHELL_PHONE_TOP_BLEED_PX = 24;
export const SHELL_PHONE_BOTTOM_BLEED_PX = 98;
/** Document scroll that lines the shell up with the screen. */
export const SHELL_PHONE_SCROLL_TO_PX =
  SHELL_SCROLL_RUNWAY_PX + SHELL_PHONE_TOP_BLEED_PX;
/** Main area height minus the visible area: negative = more room. */
const PHONE_BROWSER_RESERVED_PX =
  SHELL_TOP_BAR_PX +
  SHELL_FOOTER_PHONE_PX -
  SHELL_PHONE_TOP_BLEED_PX -
  SHELL_PHONE_BOTTOM_BLEED_PX;

/**
 * Spread into the sx of a wide table that scrolls sideways: on phones the first
 * column (the row's name) stays pinned so a scrolled row still says whose it
 * is, and header labels keep to one line.
 */
export const PHONE_TABLE_SX = {
  [SHELL_PHONE_MAX_MEDIA]: {
    "& .MuiTableHead-root .MuiTableCell-root": { whiteSpace: "nowrap" },
    "& .MuiTableRow-root > .MuiTableCell-root:first-of-type": {
      position: "sticky",
      left: 0,
      zIndex: 1,
      boxShadow: "inset -1px 0 0 rgba(0, 0, 0, 0.12)",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
      bgcolor: "background.paper",
    },
    "& .MuiTableHead-root .MuiTableCell-root:first-of-type": { zIndex: 3 },
  },
} as const;
export const SHELL_SIDEBAR_WIDTH_PX = 240;
export const SHELL_NAV_ROW_HEIGHT_PX = 48;

export const VIEWPORT_2K_MIN_WIDTH_PX = 2200;

export const VIEWPORT_2K_MIN_MEDIA = `@media (min-width: ${VIEWPORT_2K_MIN_WIDTH_PX}px)`;

export const VIEWPORT_2K_MIN_WIDTH_MATCH = `(min-width: ${VIEWPORT_2K_MIN_WIDTH_PX}px)`;
export const SHELL_SIDEBAR_WIDTH_2K_PX = 268;
export const SHELL_NAV_ROW_HEIGHT_2K_PX = 52;

export const VIEWPORT_4K_MIN_WIDTH_PX = 3600;
export const VIEWPORT_4K_MIN_MEDIA = `@media (min-width: ${VIEWPORT_4K_MIN_WIDTH_PX}px)`;

export const VIEWPORT_4K_MIN_WIDTH_MATCH = `(min-width: ${VIEWPORT_4K_MIN_WIDTH_PX}px)`;
export const SHELL_SIDEBAR_WIDTH_4K_PX = 292;
export const SHELL_NAV_ROW_HEIGHT_4K_PX = 56;
export const SHELL_NAV_GAP_PX = 8;
export const SHELL_NAV_ROW_INNER_SPACING = 1.5;
/** Sidebar rows on a short screen, so a full menu fits more often. */
export const SHELL_NAV_ROW_HEIGHT_SHORT_PX = 40;
/** The smallest row a finger hits reliably. */
export const SHELL_NAV_ROW_MIN_TOUCH_PX = 44;
/** Touch screens, whatever their width. */
export const POINTER_COARSE_MEDIA = "@media (pointer: coarse)";

export const SHELL_MAIN_CHROME_PADDING_PX = 24;

/** Top and bottom safe areas: zero in a Safari tab, the clock and home bar in a Home Screen app. */
export const SAFE_AREA_TOP = "env(safe-area-inset-top, 0px)";
export const SAFE_AREA_BOTTOM = "env(safe-area-inset-bottom, 0px)";

const viewportMinusSx = (reservedPx: number) =>
  ({
    height: `calc(100vh - (${reservedPx}px) - ${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM})`,
    "@supports (height: 100dvh)": {
      height: `calc(100dvh - (${reservedPx}px) - ${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM})`,
    },
  }) as const;

/**
 * Height of a table page under the shell's top bar and footer. Uses dvh for the
 * same reason as SHELL_VIEWPORT_HEIGHT_SX: on iOS 100vh is taller than the
 * visible area and pushes the pagination under the footer.
 */
export const TABLE_PAGE_CONTAINER_HEIGHT_SX = {
  ...viewportMinusSx(SHELL_TOP_BAR_PX + SHELL_FOOTER_PX),
  [SHELL_PHONE_MAX_MEDIA]: viewportMinusSx(
    SHELL_TOP_BAR_PX + SHELL_FOOTER_PHONE_PX,
  ),
  [SHELL_PHONE_BROWSER_MEDIA]: viewportMinusSx(PHONE_BROWSER_RESERVED_PX),
} as const;

/**
 * Compact-laptop tier. MUI's own breakpoints step lg(1200) straight to
 * xl(1536), so 1366x768 and 1920x1080 resolve to the same layout today while
 * the second has 550px more room. This is the missing rung between them, in
 * the same shape as the 2K/4K tiers above. 1440 is inclusive so a MacBook Air
 * and a windowed 1400 both get the compact treatment - above it the dense
 * tables fit as designed.
 */
export const VIEWPORT_COMPACT_MAX_WIDTH_PX = 1440;
export const VIEWPORT_COMPACT_MAX_MEDIA = `@media (max-width: ${VIEWPORT_COMPACT_MAX_WIDTH_PX}px)`;
export const VIEWPORT_COMPACT_MAX_WIDTH_MATCH = `(max-width: ${VIEWPORT_COMPACT_MAX_WIDTH_PX}px)`;

/**
 * Short-viewport tier. A 768-tall screen leaves ~625 CSS px once the browser
 * chrome and the Windows taskbar take their cut; a 900-tall one ~760. 860
 * catches both and spares a 1080-tall screen (~940).
 */
export const VIEWPORT_SHORT_MAX_HEIGHT_PX = 860;
export const VIEWPORT_SHORT_MAX_MEDIA = `@media (max-height: ${VIEWPORT_SHORT_MAX_HEIGHT_PX}px)`;
export const VIEWPORT_SHORT_MAX_HEIGHT_MATCH = `(max-height: ${VIEWPORT_SHORT_MAX_HEIGHT_PX}px)`;

const PHONE_BLEED_PX = SHELL_PHONE_TOP_BLEED_PX + SHELL_PHONE_BOTTOM_BLEED_PX;

/**
 * Height of the outer app shell. Mobile browsers count the collapsing URL bar
 * in 100vh, which cuts off the footer, so prefer 100dvh where it exists. One
 * source for every shell - the Client View one shipped without this guard.
 */
export const SHELL_VIEWPORT_HEIGHT_SX = {
  position: "relative",
  height: "100vh",
  maxHeight: "100vh",
  "@supports (height: 100dvh)": {
    height: "100dvh",
    maxHeight: "100dvh",
  },
  [SHELL_PHONE_BROWSER_MEDIA]: {
    mt: `${SHELL_SCROLL_RUNWAY_PX}px`,
    height: `calc(100vh + ${PHONE_BLEED_PX}px)`,
    maxHeight: `calc(100vh + ${PHONE_BLEED_PX}px)`,
    "@supports (height: 100dvh)": {
      height: `calc(100dvh + ${PHONE_BLEED_PX}px)`,
      maxHeight: `calc(100dvh + ${PHONE_BLEED_PX}px)`,
    },
  },
} as const;

/**
 * A box with a fixed maximum that must not out-grow the real viewport.
 * `reservedPx` is what the surrounding chrome already spends, so the box keeps
 * its designed height on a tall screen and shrinks on a laptop instead of
 * pushing the controls under it off-screen. Falls back to the flat maximum
 * where dvh is unsupported, same guard as SHELL_VIEWPORT_HEIGHT_SX.
 */
export const viewportCappedHeightSx = (
  minPx: number,
  maxPx: number,
  reservedPx: number,
) =>
  ({
    height: maxPx,
    "@supports (height: 100dvh)": {
      height: `clamp(${minPx}px, calc(100dvh - ${reservedPx}px), ${maxPx}px)`,
    },
  }) as const;

/**
 * A phone on its side: ~390px of height for the whole page. Width-based rules
 * cannot see this case - 844x390 is wider than many tablets are.
 */
export const PHONE_LANDSCAPE_MATCH =
  "(max-height: 480px) and (orientation: landscape)";
export const PHONE_LANDSCAPE_MEDIA = `@media ${PHONE_LANDSCAPE_MATCH}`;
